/* Independent Page controller. Never imports or dispatches ordinary PDP behavior. */
(() => {
  if (window.CyoShopping) return;
  const isCustom = value => /custom size|massanfertigung/i.test(String(value || ''));
  const pathname = url => new URL(url, location.origin).pathname;
  class CyoShopping {
    constructor(root) {
      this.root = root;
      this.picker = root.querySelector('[data-cyo-configurator]')?.cyoInstance;
      if (!this.picker || !root.querySelector('[data-cyo-product-data]')) return;
      this.allowed = new Map();
      Object.values(this.picker.routes).forEach(route => {
        if (!route || !route.attrs) return;
        const colors = route.colors?.length ? route.colors : [{ url: route.url, name: '' }];
        colors.forEach(color => {
          if (!color.url) return;
          const path = pathname(color.url);
          this.allowed.set(path, { route, color, path });
        });
      });
      this.sequence = 0;
      this.bind();
      this.initProduct();
      this.committedSelection = { ...this.picker.selection };
      this.committedPath = pathname(this.model.url);
      this.picker.currentProductUrl = this.model.url;
      if (this.picker.routes._cyo_invalid || !this.allowed.has(this.committedPath)) {
        this.invalid = true;
        this.status('This series is not configured for purchasing. Please view the product details.');
        this.updateVariant();
        return;
      }
      this.restoreLocation();
    }
    bind() {
      this.root.addEventListener('cyo:selection', event => this.load(event.detail.url, true, event.detail.notice));
      this.root.addEventListener('submit', event => {
        if (!event.target.matches('[data-cyo-form]')) return;
        event.preventDefault();
        this.submit();
      });
      this.root.addEventListener('change', event => {
        if (!event.target.closest('[data-cyo-host="purchase"]')) return;
        this.measurements.toggleCustomSizeFields(this.root);
        this.measurements.syncErpSizeProperty(this.root);
        this.measurements.syncMeasurePreview(this.root);
        this.updateVariant();
      });
      this.root.addEventListener('focusin', event => {
        const select = event.target.closest('[data-cyo-custom-size-box] select');
        if (!select) return;
        const description = select.closest('label')?.getAttribute('data-cyo-description');
        const hint = this.root.querySelector('[data-cyo-customize-option-desc]');
        if (hint && description) {
          const label = document.createElement('span');
          label.textContent = (select.getAttribute('data-cyo-option') || 'Measurement') + '*: ';
          hint.replaceChildren(label, document.createTextNode(description));
        }
      });
      this.root.addEventListener('click', event => {
        const measureHelp = event.target.closest('[data-cyo-custom-size-box] .faq');
        if (measureHelp) {
          event.preventDefault();
          this.chartSequence = (this.chartSequence || 0) + 1;
          const guide = measureHelp.querySelector('.big-card') || measureHelp.querySelector('.text-tip');
          const dialog = this.root.querySelector('[data-cyo-size-dialog]');
          if (guide && dialog) {
            const copy = guide.cloneNode(true);
            copy.classList.add('cyo-measure-guide');
            dialog.querySelector('[data-cyo-size-chart-content]').replaceChildren(copy);
            dialog.showModal();
          }
        }
        const quantityStep = event.target.closest('[data-cyo-quantity-step]');
        if (quantityStep && !this.loading && !this.submitting) {
          const quantity = this.form.querySelector('[name="quantity"]');
          quantity.value = Math.max(1, (Number(quantity.value) || 1) + Number(quantityStep.dataset.cyoQuantityStep));
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const media = event.target.closest('[data-cyo-media]');
        if (media) {
          this.root.querySelectorAll('[data-cyo-slide]').forEach(slide => {
            slide.hidden = slide.dataset.cyoSlide !== media.dataset.cyoMedia;
            if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
          });
          this.root.querySelectorAll('[data-cyo-media]').forEach(button => button.setAttribute('aria-pressed', String(button === media)));
        }
        const unit = event.target.closest('[data-cyo-custom-size-box] .unit-switch');
        if (unit) this.measurements.toggleCustomUnit(unit, this.root);
        if (event.target.closest('[data-cyo-sticky-submit]')) this.form.requestSubmit(this.root.querySelector('[data-cyo-submit]'));
        const chart = event.target.closest('[data-cyo-size-chart-url]');
        if (chart) this.showSizeChart(chart.getAttribute('data-cyo-size-chart-url'));
        const imageGuide = event.target.closest('[data-cyo-fancybox]');
        if (imageGuide?.getAttribute('data-cyo-src')) {
          this.chartSequence = (this.chartSequence || 0) + 1;
          const dialog = this.root.querySelector('[data-cyo-size-dialog]');
          const image = document.createElement('img');
          image.src = imageGuide.getAttribute('data-cyo-src'); image.alt = 'Measurement guide';
          dialog.querySelector('[data-cyo-size-chart-content]').replaceChildren(image);
          dialog.showModal();
        }
        const chartTab = event.target.closest('[data-cyo-size-dialog] #tabBtn, [data-cyo-size-dialog] #meaBtn');
        if (chartTab) {
          this.setSizeChartTab(chartTab.id);
        }
        if (event.target.closest('[data-cyo-size-close]')) {
          this.chartSequence = (this.chartSequence || 0) + 1;
          this.root.querySelector('[data-cyo-size-dialog]').close();
        }
      });
      this.root.addEventListener('keydown', event => {
        const chartTab = event.target.closest('[data-cyo-size-dialog] [role="tab"]');
        if (chartTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const target = event.key === 'Home' ? 'tabBtn' : event.key === 'End' ? 'meaBtn' : chartTab.id === 'tabBtn' ? 'meaBtn' : 'tabBtn';
          this.setSizeChartTab(target);
          this.root.querySelector(`[data-cyo-size-chart-content] #${target}`)?.focus();
        }
        const unit = event.target.closest('[data-cyo-custom-size-box] .unit-switch');
        if (unit && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); unit.click(); }
      });
      this.onPopState = () => this.restoreLocation();
      window.addEventListener('popstate', this.onPopState);
    }
    initProduct() {
      this.model = JSON.parse(this.root.querySelector('[data-cyo-product-data]').textContent);
      this.form = this.root.querySelector('[data-cyo-form]');
      // No standard size or custom measurements are inherited across products.
      this.root.querySelectorAll('[data-cyo-product-size-picker] input[type="radio"], .product-size-picker--variant input[type="radio"]').forEach(input => { input.checked = false; });
      this.measurements = new window.CyoMeasurements(this.root);
      this.root.querySelectorAll('[data-cyo-custom-size-box] .faq').forEach(el => {
        el.setAttribute('role', 'button');
        el.setAttribute('aria-haspopup', 'dialog');
        const field = el.closest('li')?.querySelector('select');
        el.setAttribute('aria-label', 'How to measure ' + (field?.getAttribute('data-cyo-option') || 'this measurement'));
      });
      const terms = this.root.querySelector('[data-cyo-custom-size-box] [name="return_support_checkbox"]');
      if (terms) terms.setAttribute('aria-label', 'I agree to the custom sizing terms');
      this.root.querySelectorAll('[data-cyo-delivery-start]').forEach(el => {
        const format = days => { const date = new Date(); date.setDate(date.getDate() + Number(days)); return date.toLocaleDateString(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' }); };
        el.textContent = `${format(el.dataset.cyoDeliveryStart)} – ${format(el.dataset.cyoDeliveryEnd)}`;
      });
      this.observer?.disconnect();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const sticky = this.root.querySelector('[data-cyo-shopping-sticky]');
          if (sticky) sticky.hidden = entries[0].isIntersecting;
        });
        this.observer.observe(this.root.querySelector('[data-cyo-submit]'));
      }
      this.updateVariant();
    }
    selectedOptions() {
      const initial = this.model.variants.find(variant => String(variant.id) === String(this.model.selectedVariant)) || this.model.variants[0];
      const options = [...(initial?.options || [])];
      this.root.querySelectorAll('[data-cyo-variant-option]').forEach(select => { options[Number(select.dataset.cyoVariantOption) - 1] = select.value; });
      if (this.model.sizeMode === 'variant') {
        const selected = this.root.querySelector('.product-size-picker--variant input[type="radio"]:checked');
        options[this.model.sizePosition - 1] = selected?.value || null;
      }
      return options;
    }
    updateVariant() {
      const options = this.selectedOptions();
      let variant = this.model.variants.find(item => item.options.every((value, index) => value === options[index]));
      if (variant && !this.model.customEnabled && variant.options.some(isCustom)) variant = null;
      this.variant = variant;
      const selectedPropertiesSize = this.root.querySelector('input[name="properties[Size]"]:checked');
      const sizeSelected = this.model.sizeMode !== 'properties' || !!selectedPropertiesSize;
      const disabled = !!(this.invalid || this.loading || this.submitting || !variant?.available || !sizeSelected);
      this.form.querySelector('[name="id"]').value = variant?.id || '';
      const label = this.submitting ? 'Adding…' : !variant || !sizeSelected ? 'Select a size' : !variant.available ? 'Sold out' : 'Add to bag';
      this.root.querySelectorAll('[data-cyo-submit], [data-cyo-sticky-submit]').forEach(button => { button.disabled = disabled; button.textContent = label; });
      const sizeInput = this.root.querySelector('.size-item-list input[type="radio"]:checked');
      const selectedLabel = this.root.querySelector('[data-cyo-size-selected-label]');
      if (selectedLabel) selectedLabel.textContent = sizeInput ? (isCustom(sizeInput.value) ? 'Custom Size' : sizeInput.value.split('(')[0]) : '';
      const priceVariant = variant || this.model.variants.find(item => String(item.id) === String(this.model.selectedVariant));
      if (priceVariant) {
        const format = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.model.currency }).format(cents / 100);
        this.root.querySelectorAll('[data-cyo-price], [data-cyo-sticky-price]').forEach(el => { el.textContent = format(priceVariant.price); });
        const compare = this.root.querySelector('[data-cyo-compare-price]');
        compare.hidden = !(priceVariant.compare_at_price > priceVariant.price);
        compare.textContent = priceVariant.compare_at_price ? format(priceVariant.compare_at_price) : '';
      }
      if (this.model.sizeMode === 'variant') {
        this.root.querySelectorAll('.product-size-picker--variant input[type="radio"]').forEach(input => {
          input.disabled = (!this.model.customEnabled && isCustom(input.value)) || !this.model.variants.some(item => item.available && item.options.every((value, index) => index === this.model.sizePosition - 1 ? value === input.value : value === options[index]));
        });
      }
    }
    findLocationTarget() {
      const handle = new URL(location.href).searchParams.get('product');
      if (!handle) return this.allowed.get(pathname(this.root.dataset.cyoDefaultUrl));
      return Array.from(this.allowed.values()).find(entry => decodeURIComponent(entry.path.split('/').pop()) === handle);
    }
    restoreLocation() {
      const target = this.findLocationTarget();
      if (!target) {
        this.status('The requested combination is not in this series. Showing the previous product.');
        this.writeLocation(false);
        return;
      }
      if (target.path !== this.committedPath || this.loading) this.load(target.path, false);
    }
    writeLocation(push) {
      const url = new URL(location.href);
      url.searchParams.set('product', decodeURIComponent(this.committedPath.split('/').pop()));
      const next = url.pathname + url.search + url.hash;
      if (push && next !== location.pathname + location.search + location.hash) history.pushState({}, '', next);
      else if (!push) history.replaceState({}, '', next);
    }
    async load(url, push = true, notice = '') {
      if (this.submitting) { this.resetPicker(); this.status('Please wait until this item has been added.'); return; }
      const target = this.allowed.get(pathname(url));
      if (!target) { this.resetPicker(); this.status('This combination is unavailable.'); return; }
      this.abort?.abort();
      this.abort = new AbortController();
      const sequence = ++this.sequence;
      this.loading = true;
      this.root.setAttribute('data-cyo-loading', 'true');
      this.root.setAttribute('aria-busy', 'true');
      this.updateVariant();
      this.status('Loading your dress…');
      try {
        const request = new URL(target.path, location.origin);
        request.searchParams.set('view', 'cyo-fragment');
        const response = await fetch(request, { signal: this.abort.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to load this product');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (sequence !== this.sequence) return;
        const data = doc.querySelector('[data-cyo-product-data]');
        if (!data || pathname(JSON.parse(data.textContent).url) !== target.path) throw new Error('Product response does not match your selection');
        const parts = ['media', 'header', 'purchase', 'details'].map(part => [this.root.querySelector(`[data-cyo-host="${part}"]`), doc.querySelector(`[data-cyo-fragment="${part}"]`)]);
        if (parts.some(([host, content]) => !host || !content)) throw new Error('Product content is incomplete');
        // Validate all fragments before replacing any part of the current product.
        parts.forEach(([host, content]) => host.replaceChildren(...Array.from(content.childNodes)));
        this.committedPath = target.path;
        this.picker.selection = { ...target.route.attrs, [this.picker.colorAxis?.key || 'color']: target.color.name };
        this.committedSelection = { ...this.picker.selection };
        this.picker.render({ skipNavigate: true });
        this.initProduct();
        this.writeLocation(push);
        this.status(notice);
      } catch (error) {
        if (sequence !== this.sequence || error.name === 'AbortError') return;
        this.resetPicker();
        if (!push) this.writeLocation(false);
        this.status('Unable to load this dress. Your previous product is unchanged. Please try again.');
      } finally {
        if (sequence === this.sequence) {
          this.loading = false;
          this.root.removeAttribute('data-cyo-loading');
          this.root.removeAttribute('aria-busy');
          this.updateVariant();
        }
      }
    }
    resetPicker() { this.picker.selection = { ...this.committedSelection }; this.picker.render({ skipNavigate: true }); }
    status(message) { this.root.querySelector('[data-cyo-shopping-status]').textContent = message || ''; }
    error(message) { const el = this.root.querySelector('[data-cyo-form-error]'); el.textContent = message; el.hidden = !message; }
    async submit() {
      if (this.loading || this.submitting || this.invalid) return;
      this.error('');
      this.updateVariant();
      if (!this.variant?.available || !this.form.checkValidity()) { this.form.reportValidity(); this.error('Please select an available size and quantity.'); return; }
      if (this.model.sizeMode === 'properties' && !this.root.querySelector('input[name="properties[Size]"]:checked')) { this.error('Please select a size.'); return; }
      if (this.measurements.isCustomSizeSelected(this.root) && (!this.model.customEnabled || !this.root.querySelector('[data-cyo-custom-size-box]'))) { this.error('Custom sizing is not available for this product.'); return; }
      this.measurements.syncErpSizeProperty(this.root);
      if (!this.measurements.validateAndGenerateCustomSize(this.root)) return;
      this.submitting = true;
      this.updateVariant();
      const drawer = document.querySelector('cart-notification');
      drawer?.setActiveElement(this.root.querySelector('[data-cyo-submit]'));
      const data = new FormData(this.form);
      data.delete('cyo-size-' + this.model.id);
      data.set('id', String(this.variant.id));
      if (drawer) data.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
      data.set('sections_url', location.pathname);
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to add this item');
        if (drawer) {
          try {
            await this.presentCart(state, drawer);
          } catch (error) {
            this.status('Added to your cart. Please open the cart to view your item.');
          }
        } else this.status('Added to your cart.');
      } catch (error) { this.error(error.message || 'Unable to add this item. Please try again.'); }
      finally { this.submitting = false; this.updateVariant(); }
    }
    async presentCart(state, drawer) {
      const sectionIds = drawer.getSectionsToRender().map(section => section.id);
      if (sectionIds.some(id => !state.sections?.[id])) {
        // A successful cart/add can still return null HTML. Refresh; never add again.
        const url = new URL((window.Shopify?.routes?.root || '/') + 'cart', location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to refresh cart');
        state.sections = { ...state.sections, ...await response.json() };
        if (sectionIds.some(id => !state.sections[id])) throw new Error('Cart content is incomplete');
      }
      // Keep the existing drawer's design, recommendations, progress and open logic.
      drawer.renderContents(state);
    }
    setSizeChartTab(buttonId) {
      const content = this.root.querySelector('[data-cyo-size-chart-content]');
      const selectedPanel = buttonId === 'meaBtn' ? 'chartImg' : 'chartTab';
      content.querySelector('.switch-button')?.setAttribute('role', 'tablist');
      content.querySelectorAll('#chartTab, #chartImg').forEach(panel => {
        const active = panel.id === selectedPanel;
        panel.hidden = !active;
        panel.classList.toggle('active-tab', active);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', active ? buttonId : panel.id === 'chartTab' ? 'tabBtn' : 'meaBtn');
      });
      content.querySelectorAll('#tabBtn, #meaBtn').forEach(button => {
        const active = button.id === buttonId;
        button.classList.toggle('focus', active);
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', String(active));
        button.setAttribute('aria-controls', button.id === 'tabBtn' ? 'chartTab' : 'chartImg');
        button.tabIndex = active ? 0 : -1;
      });
    }
    async showSizeChart(url) {
      const sequence = (this.chartSequence || 0) + 1;
      this.chartSequence = sequence;
      const dialog = this.root.querySelector('[data-cyo-size-dialog]');
      const content = dialog.querySelector('[data-cyo-size-chart-content]');
      content.textContent = 'Loading…';
      dialog.showModal();
      try {
        const response = await fetch(new URL(url, location.origin));
        if (!response.ok) throw new Error();
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        doc.querySelectorAll('script').forEach(script => script.remove());
        if (!content.isConnected || sequence !== this.chartSequence || !dialog.open) return;
        content.innerHTML = doc.body.innerHTML;
        this.setSizeChartTab('tabBtn');
      } catch (error) {
        if (content.isConnected && sequence === this.chartSequence) content.textContent = 'Unable to load the size guide. Please try again.';
      }
    }
    destroy() { this.abort?.abort(); this.observer?.disconnect(); window.removeEventListener('popstate', this.onPopState); }
    static init(root = document) {
      window.CyoConfigurator?.initAll(root);
      root.querySelectorAll('[data-cyo-shopping]:not([data-cyo-shopping-ready])').forEach(el => {
        el.setAttribute('data-cyo-shopping-ready', 'true');
        el.cyoShopping = new CyoShopping(el);
      });
    }
  }
  window.CyoShopping = CyoShopping;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => CyoShopping.init());
  else CyoShopping.init();
  document.addEventListener('shopify:section:load', event => CyoShopping.init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-cyo-shopping]').forEach(root => root.cyoShopping?.destroy()));
})();
